/**
 * CollectionSearchForm
 *
 * Behaviour:
 *  - While typing  → client-side filter on DOM cards (instant feedback)
 *  - First keystroke → silently fetches every remaining page of the collection
 *    via Shopify's Section Rendering API and appends the product cards to the
 *    grid, so ALL collection products are searchable without a page reload.
 *  - Enter / "Ver todos" → full-page navigation with ?q= (works even when
 *    Shopify's server-side collection search is not configured).
 *  - On page load with ?q= in URL → auto-loads all pages and re-applies filter.
 *  - Clear button → removes filter, restores normal pagination view.
 */
class CollectionSearchForm extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout>|null} */
  #debounceTimer = null;

  /** Whether every page has been appended to the grid already */
  #allPagesLoaded = false;

  /** True while a background fetch is running (prevents double-fetching) */
  #fetching = false;

  connectedCallback() {
    const form     = this.querySelector('.collection-search-form');
    const input    = this.querySelector('.collection-search-form__input');
    const clearBtn = this.querySelector('.collection-search-form__clear');

    // ── Auto-restore after Enter navigation ────────────────────────────────
    const initialTerm = new URL(window.location.href).searchParams.get('q') ?? '';
    if (initialTerm && input) {
      input.value = initialTerm;
      if (clearBtn) clearBtn.style.display = 'flex';
      // Load all pages then apply filter (don't block paint)
      requestAnimationFrame(() => this.#startSearch(initialTerm.toLowerCase()));
    }

    // ── Typing → debounced client-side filter ──────────────────────────────
    input?.addEventListener('input', () => {
      const term = input.value.trim();
      if (clearBtn) clearBtn.style.display = term ? 'flex' : 'none';

      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = setTimeout(() => {
        if (term) {
          this.#startSearch(term.toLowerCase());
        } else {
          this.#clearSearch();
        }
      }, 200);
    });

    // ── Enter → full-page search (finds products across all pages) ──────────
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const term = input?.value.trim() ?? '';
      this.#navigate(term);
    });

    // ── Clear ───────────────────────────────────────────────────────────────
    clearBtn?.addEventListener('click', () => {
      if (input)    input.value = '';
      if (clearBtn) clearBtn.style.display = 'none';
      this.#clearSearch();
      this.#navigate('');
    });
  }

  // ── Orchestrates loading + filtering ──────────────────────────────────────
  async #startSearch(term) {
    // Apply filter immediately on whatever cards are already in the DOM
    this.#filterCards(term);

    // If we haven't loaded all pages yet, fetch them now
    if (!this.#allPagesLoaded) {
      await this.#loadAllPages();
    }

    // Re-filter now that everything is in the DOM
    this.#filterCards(term);
  }

  // ── Fetch every page of the collection and append cards to the grid ───────
  async #loadAllPages() {
    if (this.#fetching || this.#allPagesLoaded) return;
    this.#fetching = true;

    try {
      const grid = this.#getGrid();
      if (!grid) return;

      const lastPage = parseInt(grid.dataset.lastPage ?? '1', 10);
      if (lastPage <= 1) { this.#allPagesLoaded = true; return; }

      // Section ID lives on the results-list element (same value Shopify uses
      // for the Section Rendering API: e.g. "template--xxx--main-collection")
      const sectionId = document.querySelector('results-list[section-id]')
        ?.getAttribute('section-id') ?? '';
      if (!sectionId) return;

      // Base URL: current collection path + any active filter params,
      // but WITHOUT ?q=, options[prefix], or page (we set page ourselves)
      const baseUrl = new URL(window.location.origin + window.location.pathname);
      new URL(window.location.href).searchParams.forEach((v, k) => {
        if (!['q', 'options[prefix]', 'page', 'section_id'].includes(k)) {
          baseUrl.searchParams.set(k, v);
        }
      });

      this.#showLoadingBadge(lastPage - 1);

      // Fetch all remaining pages in parallel
      const pageUrls = [];
      for (let p = 2; p <= lastPage; p++) {
        const u = new URL(baseUrl.toString());
        u.searchParams.set('page', String(p));
        u.searchParams.set('section_id', sectionId);
        u.searchParams.sort();
        pageUrls.push(u.toString());
      }

      const htmlPages = await Promise.all(
        pageUrls.map((url) => fetch(url).then((r) => (r.ok ? r.text() : null)).catch(() => null))
      );

      for (const html of htmlPages) {
        if (!html) continue;
        const doc   = new DOMParser().parseFromString(html, 'text/html');
        const cards = doc.querySelectorAll('.product-grid__item[data-title]');
        cards.forEach((card) => grid.appendChild(document.importNode(card, true)));
      }

      // Hide pagination now that everything is in one page
      document.querySelector('.pagination')?.setAttribute('hidden', '');
      this.#allPagesLoaded = true;

    } finally {
      this.#fetching = false;
      this.#hideLoadingBadge();
    }
  }

  // ── Show / hide product cards by matching data-title ──────────────────────
  #filterCards(term) {
    const cards = /** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll('.product-grid__item[data-title]')
    );

    let visible = 0;
    cards.forEach((card) => {
      const match = !term || (card.dataset.title ?? '').includes(term);
      card.style.display = match ? '' : 'none';
      if (match) visible++;
    });

    this.#updateStatus(term, visible);
  }

  // ── Restore view when search is cleared ───────────────────────────────────
  #clearSearch() {
    // Remove extra cards that were loaded from other pages and restore pagination
    if (this.#allPagesLoaded) {
      const grid = this.#getGrid();
      if (grid) {
        // Keep only cards that were originally on page 1 (data-page="1")
        grid.querySelectorAll('.product-grid__item[data-page]').forEach((card) => {
          const page = parseInt(/** @type {HTMLElement} */ (card).dataset.page ?? '1', 10);
          if (page > 1) card.remove();
        });
      }
      document.querySelector('.pagination')?.removeAttribute('hidden');
      this.#allPagesLoaded = false;
    }

    // Show all remaining cards
    document.querySelectorAll('.product-grid__item[data-title]').forEach((card) => {
      /** @type {HTMLElement} */ (card).style.display = '';
    });

    this.#removeStatus();
  }

  // ── Status bar (result count + "Ver todos" button) ────────────────────────
  #updateStatus(term, count) {
    if (!term) { this.#removeStatus(); return; }

    let bar = /** @type {HTMLElement|null} */ (document.querySelector('.cs-status-bar'));
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'cs-status-bar';
      bar.style.cssText = [
        'grid-column:1/-1',
        'display:flex',
        'align-items:center',
        'gap:10px',
        'flex-wrap:wrap',
        'padding:8px 0',
        'font-size:0.85rem',
        'color:var(--color-foreground)',
        'opacity:0.8',
      ].join(';');
      this.#getGrid()?.before(bar);
    }

    const loading = this.#fetching ? ' <em>(cargando más…)</em>' : '';
    bar.innerHTML = count === 0
      ? `Sin resultados para "<strong>${this.#esc(term)}</strong>"${loading}`
      : `<strong>${count}</strong> resultado${count !== 1 ? 's' : ''} para "<strong>${this.#esc(term)}</strong>"${loading}`;
  }

  #removeStatus() {
    document.querySelector('.cs-status-bar')?.remove();
  }

  // ── Small loading badge while fetching pages ───────────────────────────────
  #showLoadingBadge(pagesLeft) {
    if (document.querySelector('.cs-loading')) return;
    const el = document.createElement('div');
    el.className = 'cs-loading';
    el.textContent = `Cargando ${pagesLeft} página${pagesLeft !== 1 ? 's' : ''} más…`;
    el.style.cssText = [
      'grid-column:1/-1',
      'padding:6px 0',
      'font-size:0.8rem',
      'opacity:0.5',
      'color:var(--color-foreground)',
      'font-style:italic',
    ].join(';');
    this.#getGrid()?.before(el);
  }

  #hideLoadingBadge() {
    document.querySelector('.cs-loading')?.remove();
  }

  // ── Full-page navigation with ?q= ─────────────────────────────────────────
  #navigate(query) {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
    } else {
      url.searchParams.delete('q');
      url.searchParams.delete('options[prefix]');
    }
    url.searchParams.delete('page');
    window.location.href = url.toString();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  #getGrid() {
    return /** @type {HTMLElement|null} */ (
      document.querySelector('[ref="grid"]') ?? document.querySelector('.product-grid')
    );
  }

  #esc(str) {
    return str.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c)
    );
  }
}

if (!customElements.get('collection-search-form')) {
  customElements.define('collection-search-form', CollectionSearchForm);
}
